import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Eye, FileText, Loader2, Pencil, Search, Trash2, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChatMarkdown } from '@/components/chat/ChatMarkdown';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { BUILT_IN_TEMPLATES, TEMPLATE_CATEGORIES, type TemplateCategory } from '@/lib/projectTemplates';
import { useLocalization } from '@/hooks/useLocalization';

const DOCS_BUCKET = 'project-documents';
const MAX_TEMPLATE_BYTES = 200 * 1024;

interface TemplateCard {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  content: string;
  builtIn: boolean;
  createdBy?: string;
  useCount?: number;
  lastUsedAt?: string | null;
}

const categoryClass = (c: TemplateCategory) => ({
  planning: 'bg-primary/10 text-primary border-primary/30',
  meeting: 'bg-success/10 text-success border-success/30',
  communication: 'bg-warning/10 text-warning border-warning/30',
  tracking: 'bg-destructive/10 text-destructive border-destructive/30',
  reporting: 'bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/30',
}[c]);

const fileSafe = (name: string) => name.replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '-') || 'template';

interface Props {
  /** Called after a document has been created from a template. */
  onDocumentCreated: () => void;
  canManageAll: boolean;
}

const TemplatesTab = ({ onDocumentCreated, canManageAll }: Props) => {
  const { tx, t } = useLocalization();
  const { user, tenantId } = useAuth();
  const { toast } = useToast();
  const [custom, setCustom] = useState<TemplateCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<'all' | TemplateCategory>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [preview, setPreview] = useState<TemplateCard | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Set when the dialog edits an existing team template; null when it creates one. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', description: '', category: 'planning' as TemplateCategory, content: '' });
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('project_templates')
      .select('id, name, description, category, content, created_by, use_count, last_used_at')
      .order('created_at', { ascending: false });
    if (error) {
      toast({ title: t('Could not load custom templates'), description: error.message, variant: 'destructive' });
    } else {
      setCustom((data ?? []).map((tpl) => ({
        id: tpl.id, name: tpl.name, description: tpl.description, category: tpl.category as TemplateCategory,
        content: tpl.content, builtIn: false, createdBy: tpl.created_by, useCount: tpl.use_count, lastUsedAt: tpl.last_used_at,
      })));
    }
    setLoading(false);
  }, [toast, t]);

  useEffect(() => { load(); }, [load]);

  const cards = useMemo(() => {
    const all: TemplateCard[] = [...custom, ...BUILT_IN_TEMPLATES.map((tpl) => ({ ...tpl, builtIn: true }))];
    const q = search.trim().toLowerCase();
    return all.filter((tpl) =>
      (category === 'all' || tpl.category === category) &&
      (!q || tpl.name.toLowerCase().includes(q) || tpl.description.toLowerCase().includes(q)));
  }, [custom, search, category]);

  const createFromTemplate = async (tpl: TemplateCard) => {
    if (!user) return;
    setBusyId(tpl.id);
    try {
      const blob = new Blob([tpl.content], { type: 'text/markdown' });
      const fileName = `${fileSafe(tpl.name)}.md`;
      const path = `${user.id}/${Date.now()}-${fileName}`;
      const { error: upErr } = await supabase.storage.from(DOCS_BUCKET).upload(path, blob, { contentType: 'text/markdown' });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase.from('project_documents').insert({
        user_id: user.id, tenant_id: tenantId, name: fileName, file_path: path,
        mime_type: 'text/markdown', size_bytes: blob.size,
      });
      if (insErr) throw insErr;
      if (!tpl.builtIn) {
        await supabase.rpc('record_project_template_use', { _template_id: tpl.id });
        load();
      }
      toast({ title: t('Document created'), description: t('{0} is in the Documents tab.', { 0: fileName }) });
      onDocumentCreated();
    } catch (err) {
      toast({ title: t('Could not create document'), description: err instanceof Error ? err.message : t('Unknown error'), variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_TEMPLATE_BYTES) {
      toast({ title: t('File too large'), description: t('Templates must be 200 KB or smaller.'), variant: 'destructive' });
      return;
    }
    const text = await file.text();
    setForm((f) => ({ ...f, content: text, name: f.name || file.name.replace(/\.[^.]+$/, '') }));
  };

  const emptyForm = { name: '', description: '', category: 'planning' as TemplateCategory, content: '' };

  const openNew = () => { setEditingId(null); setForm(emptyForm); setUploadOpen(true); };

  /** Team templates are edited in place; built-ins open as a copy that saves as a new team template. */
  const openEdit = (tpl: TemplateCard) => {
    setEditingId(tpl.builtIn ? null : tpl.id);
    setForm({ name: tpl.builtIn ? `${tpl.name} (copy)` : tpl.name, description: tpl.description, category: tpl.category, content: tpl.content });
    setUploadOpen(true);
  };

  const save = async () => {
    if (!user) return;
    setSaving(true);
    const fields = {
      name: form.name.trim(),
      description: form.description.trim(),
      category: form.category,
      content: form.content,
    };
    let error: { message: string } | null;
    if (editingId) {
      // RLS filters rows the user may not edit instead of raising, so check a row came back.
      const res = await supabase.from('project_templates').update(fields).eq('id', editingId).select('id');
      error = res.error ?? (res.data?.length ? null : { message: 'Only the author or an admin can edit this template.' });
    } else {
      ({ error } = await supabase.from('project_templates').insert({ created_by: user.id, ...fields }));
    }
    setSaving(false);
    if (error) {
      toast({ title: t('Could not save template'), description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: editingId ? t('Template updated') : t('Template saved'), description: fields.name });
    setUploadOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    load();
  };

  const remove = async (tpl: TemplateCard) => {
    const { error } = await supabase.from('project_templates').delete().eq('id', tpl.id);
    if (error) {
      toast({ title: t('Could not delete template'), description: error.message, variant: 'destructive' });
      return;
    }
    setCustom((prev) => prev.filter((c) => c.id !== tpl.id));
    toast({ title: t('Template deleted'), description: tpl.name });
  };

  return (
    <div className="space-y-6">
      <div className="nexus-card p-4 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-foreground">{t('Project Templates')}</h3>
          <p className="text-sm text-muted-foreground">{t('Start a document from a standard layout, or upload your own for the team')}</p>
        </div>
        <Button size="sm" onClick={openNew}>
          <Upload className="w-4 h-4 me-2" />
          
          {t('Upload Template')}
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder={t('Search templates...')} value={search} onChange={(e) => setSearch(e.target.value)} className="ps-10" />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as 'all' | TemplateCategory)}
          aria-label={t('Filter by category')}
          className="h-10 px-3 rounded-md border border-input bg-background text-sm capitalize"
        >
          <option value="all">{t('All Categories')}</option>
          {TEMPLATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : cards.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">{t('No templates match your search.')}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {cards.map((tpl) => (
            <div key={tpl.id} className="nexus-card p-5 flex flex-col hover:border-primary/30 transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <FileText className="w-6 h-6" />
                </div>
                <Badge variant="outline" className={cn('capitalize text-xs', categoryClass(tpl.category))}>{tpl.category}</Badge>
              </div>
              <h4 className="font-semibold text-foreground mb-2">{tpl.name}</h4>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{tx(tpl.description) || t('No description')}</p>
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-4 mt-auto">
                {tpl.builtIn ? <span>{t('Built-in')}</span> : <span>{t('Team template')}</span>}
                {!tpl.builtIn && (
                  <span>
                    {tpl.useCount ? t('Used {0}×{1}', { 0: tpl.useCount, 1: tpl.lastUsedAt ? `, ${formatDistanceToNow(new Date(tpl.lastUsedAt), { addSuffix: true })}` : '' }) : t('Not used yet')}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1" onClick={() => createFromTemplate(tpl)} disabled={busyId === tpl.id}>
                  {busyId === tpl.id ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Copy className="w-4 h-4 me-2" />}
                  
                  {t('Use Template')}
                </Button>
                <Button size="sm" variant="outline" aria-label={t('Preview {0}', { 0: tpl.name })} onClick={() => setPreview(tpl)}>
                  <Eye className="w-4 h-4" />
                </Button>
                {(tpl.builtIn || canManageAll || tpl.createdBy === user?.id) && (
                  <Button
                    size="sm" variant="outline"
                    aria-label={tpl.builtIn ? t('Customize {0}', { 0: tpl.name }) : t('Edit {0}', { 0: tpl.name })}
                    title={tpl.builtIn ? t('Customize as a team template') : t('Edit')}
                    onClick={() => openEdit(tpl)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
                {!tpl.builtIn && (canManageAll || tpl.createdBy === user?.id) && (
                  <Button size="sm" variant="outline" aria-label={t('Delete {0}', { 0: tpl.name })} onClick={() => remove(tpl)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{preview?.name}</DialogTitle>
            <DialogDescription>{tx(preview?.description)}</DialogDescription>
          </DialogHeader>
          {preview && <ChatMarkdown text={preview.content} />}
          <DialogFooter>
            {preview && (preview.builtIn || canManageAll || preview.createdBy === user?.id) && (
              <Button variant="outline" onClick={() => { const p = preview; setPreview(null); if (p) openEdit(p); }}>
                <Pencil className="w-4 h-4 me-2" />{preview.builtIn ? t('Customize') : t('Edit')}
              </Button>
            )}
            <Button onClick={() => { const p = preview; setPreview(null); if (p) createFromTemplate(p); }}>
              <Copy className="w-4 h-4 me-2" />{t('Use Template')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingId ? t('Edit Template') : t('Upload Template')}</DialogTitle>
            <DialogDescription>{editingId ? t('Changes apply for everyone in your organisation.') : t('Shared with everyone in your organisation. Markdown or plain text.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-name">{t('Name')}</Label>
              <Input id="tpl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-desc">{t('Description')}</Label>
              <Input id="tpl-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-cat">{t('Category')}</Label>
              <select
                id="tpl-cat"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value as TemplateCategory })}
                className="h-10 w-full px-3 rounded-md border border-input bg-background text-sm capitalize"
              >
                {TEMPLATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="tpl-content">{t('Content')}</Label>
                <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => fileRef.current?.click()}>
                  
                  {t('Load from a .md or .txt file')}
                </Button>
              </div>
              <Textarea id="tpl-content" rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="font-mono text-xs" />
              <input ref={fileRef} type="file" accept=".md,.markdown,.txt,text/markdown,text/plain" className="hidden" onChange={onFile} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>{t('Cancel')}</Button>
            <Button onClick={save} disabled={saving || !form.name.trim() || !form.content.trim()}>
              {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}{editingId ? t('Save changes') : t('Save template')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesTab;
